import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAnimateOnScroll } from '../hooks';

const architectures = [
  {
    name: 'Air-Gapped GPU Platform',
    stack: 'NVIDIA BCM / Run:AI / Tanzu',
    spec: 'Designed production AI inference infrastructure isolated from public networks. Managed end-to-end GPU node onboarding, driver compatibility validation, and Knative/Kserve deployments.',
    impacts: ['Optimized GPU scheduling & allocation', 'Reduced inference latency bottlenecks']
  },
  {
    name: 'Nested OpenShift & Tanzu',
    stack: 'OpenShift / VMware Cloud Foundation',
    spec: 'Architected and deployed enterprise-grade nested Kubernetes clusters. Enabled flexible, multi-tenant environment provisioning with strict workload isolation and resource optimization.',
    impacts: ['Built from bare-metal bootstrap to prod', 'Executed zero-downtime cluster upgrades']
  },
  {
    name: 'High-Availability Multi-Cloud',
    stack: 'AWS / Azure / GCP / EKS',
    spec: 'Engineered full-stack infrastructure for eCommerce and FinTech domains. Implemented dynamic edge caching (Cloudflare/CloudFront) and robust load balancing.',
    impacts: ['Sustained 20,000+ concurrent users', 'Led 30+ zero-downtime cloud migrations']
  },
  {
    name: 'Zero-Access GitOps Transformation',
    stack: 'ArgoCD / GitLab CI / OIDC',
    spec: 'Converted traditional K8s operations into a strict Git-driven model. Eliminated direct cluster access for developers, enforcing deployment consistency and trace auditability.',
    impacts: ['Standardized multi-cluster workflows', 'Integrated VPN-free & SSO security models']
  },
  {
    name: 'Dark-Network Observability',
    stack: 'OpenTelemetry / Prometheus / ELK',
    spec: 'Deployed centralized monitoring and alerting systems for secure government environments. Performed deep root cause analysis (RCA) on distributed workloads to maintain SLAs.',
    impacts: ['Resolved deep Calico CNI networking issues', 'Diagnosed node instability & cluster failures']
  },
  {
    name: 'Distributed Ledger Infra',
    stack: 'Hyperledger Fabric / n8n',
    spec: 'Designed secure blockchain infrastructure for government land registry systems. Automated smart contract deployments and integrated event-driven workflow orchestrations.',
    impacts: ['Automated DApp lifecycles', 'Integrated internal APIs via webhooks']
  }
];

// Extracted ProjectCard to handle individual mobile toggle state
const ProjectCard = ({ arch, inView, idx }: { arch: any, inView: boolean, idx: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: idx * 0.15 }}
      className="group bg-[#0a0a0a] border border-zinc-800/80 hover:border-zinc-500 transition-all cursor-crosshair flex flex-col h-full overflow-hidden shadow-xl"
    >
      {/* Card Header (Always Visible) */}
      <div className="bg-zinc-900/30 border-b border-zinc-800/80 px-6 py-4">
        <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mb-3">
          [ {arch.stack} ]
        </div>
        <h3 className="text-base sm:text-lg font-mono text-white tracking-tight group-hover:text-zinc-200 transition-colors leading-snug">
          {arch.name}
        </h3>
      </div>

      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="md:hidden w-full bg-zinc-900/20 text-zinc-400 hover:text-white flex justify-between items-center px-6 py-3 font-mono text-[10px] tracking-widest uppercase border-b border-zinc-800/50"
      >
        <span>{isExpanded ? 'View Less' : 'View More'}</span>
        <span>{isExpanded ? '[-]' : '[+]'}</span>
      </button>

      {/* Card Body (Collapsible on mobile, always visible on desktop) */}
      <div className={`${isExpanded ? 'flex' : 'hidden'} md:flex px-6 py-5 flex-col flex-grow`}>
        <p className="text-sm text-zinc-400 leading-relaxed mb-6 font-sans">
          {arch.spec}
        </p>
        
        {/* Terminal-style Impact Output */}
        <div className="mt-auto">
          <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest mb-3 border-t border-zinc-800/50 pt-4">
            Execution Results:
          </div>
          <ul className="space-y-2">
            {arch.impacts.map((impact: string, i: number) => (
              <li key={i} className="flex items-start gap-2 font-mono text-xs text-zinc-300">
                <span className="text-zinc-600 mt-[1px] select-none">{'>'}</span>
                <span className="leading-tight">{impact}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

const Projects: React.FC = () => {
  const { ref, inView } = useAnimateOnScroll();

  return (
    <section id="projects" className="py-16 sm:py-20 bg-[#050505] px-4 sm:px-8 md:px-12 border-t border-zinc-900 overflow-hidden">
      <div className="max-w-6xl mx-auto" ref={ref}>
        
        {/* Main Section Heading */}
        <div className="mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-mono font-medium text-white tracking-tight mb-2">
            ~/Projects
          </h2>
          <div className="text-zinc-500 font-mono text-xs sm:text-sm tracking-widest uppercase">
            03 // Key Architecture & Infrastructure Contributions
          </div>
        </div>

        {/* CSS Grid for the Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {architectures.map((arch, i) => (
            <ProjectCard key={i} arch={arch} inView={inView} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;